import { useTranslation } from "react-i18next";
import { App } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useState } from "react";

import { usePatchDataBlockMutation } from "@/redux/service";
import { useEvidenceContributors } from "@/domains/evidence/hooks/useEvidenceFields";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import { useDataBlockPermissions } from "@/domains/datablocks/hooks/useDataBlockPermissions";
import { BaseUserResponse } from "@/redux/users/types";
import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import UserAvatar from "@/domains/users/components/UserAvatar";
import AddContributorsModal from "@/features/datablocks/details/components/AddContributorsModal";
import { DataBlockSidebarProps } from "@/features/datablocks/details/types";
import DataBlockVersions from "@/features/datablocks/details/components/DataBlockVersions";
import BaseDivider from "@/components_v2/Divider/BaseDivider";

import "../styles.css";

export const DataBlockSidebar = ({
  dataBlock,
  dataTemplate,
  showCtas,
}: DataBlockSidebarProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [patchDataBlock] = usePatchDataBlockMutation();
  const [contributorModalOpen, setContributorModalOpen] = useState(false);
  const contributors = useEvidenceContributors(dataBlock);
  const currentUser = useSelector(selectCurrentUser);
  const { canEdit } = useDataBlockPermissions({
    dataBlock,
    dataTemplate,
    currentUser,
  });

  const deleteContributor = (deletedContributorId: number) => {
    const data = {
      fieldValues: [
        {
          field: dataBlock.fieldsMapping.contributors,
          value: (contributors.data as BaseUserResponse[])
            .filter((i) => i.id !== deletedContributorId)
            .map((i) => i.id),
        },
      ],
    };
    patchDataBlock({ id: dataBlock.id, data })
      .unwrap()
      .then(() =>
        notification.success({
          message: t("datablocks.labels.contributorsDeleted"),
        }),
      );
  };

  return (
    <div className="data-block-sidebar flex flex-col gap-400 full-height scrollbar-neutral">
      <div className="flex flex-col gap-200">
        <div className="flex flex-wrap justify-between items-center">
          <h2 className="header-m-medium">
            {t("datablocks.labels.contributors")}
          </h2>

          {canEdit && showCtas && (
            <BaseButton
              variant={BUTTON_VARIANTS.LINK}
              size={BUTTON_SIZES.SMALL}
              leadingIcon={<PlusOutlined />}
              onClick={() => setContributorModalOpen(true)}
            >
              {t("datablocks.labels.addContributors")}
            </BaseButton>
          )}
        </div>

        <div className="flex flex-col gap-200">
          {contributors.isLoading ? (
            <div className="flex flex-col gap-200">
              <BaseSpinner size="small" />
            </div>
          ) : (
            (contributors.data as BaseUserResponse[]).map((contributor) => (
              <div
                key={contributor.id}
                className="flex gap-200 justify-between"
              >
                <div className="data-block-sidebar-contributors-items-details flex gap-200 flex-1 items-center full-width">
                  <UserAvatar user={contributor} />
                  <span className="data-block-sidebar-contributors-items-names flex-1">
                    {contributor.fullName}
                  </span>
                </div>

                {canEdit && showCtas && (
                  <DeleteOutlined
                    onClick={() => deleteContributor(contributor.id)}
                  />
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <BaseDivider />

      <div className="flex flex-col gap-300">
        <h2 className="header-m-medium">
          {t("datablocks.labels.referencesAndEditions")}
        </h2>

        <DataBlockVersions dataBlockId={dataBlock.id} />
      </div>

      <AddContributorsModal
        open={contributorModalOpen}
        setOpen={setContributorModalOpen}
        dataBlock={dataBlock}
        currentContributorIds={
          contributors.isLoading
            ? []
            : (contributors.data as BaseUserResponse[]).map((i) => i.id)
        }
      />
    </div>
  );
};
